import { useEffect, useMemo, useState } from "react";
import {
  getNotificationSettings,
  getNotifications,
  markNotificationRead,
  updateNotificationSettings,
} from "../api/instateamNotificationsApi";
import { AppIcon } from "../components/InstateamEmployeeComponents";

const TABS = [
  ["all", "All"],
  ["unread", "Unread"],
  ["announcements", "Announcements"],
  ["tasks", "Tasks"],
  ["chat", "Chat"],
  ["leave", "Leaves"],
  ["system", "System"],
];

const PREFERENCES = [
  ["complaints", "Complaints", "Get notified about complaint updates and responses", "chat"],
  ["chat", "Chat Messages", "Get notified when a new chat message arrives", "messageSquare"],
  ["leave", "Leave Requests", "Get notified about leave approvals or rejections", "checkCircle"],
  ["tasks", "Tasks", "Get notified about new tasks and task updates", "clipboard"],
  ["announcements", "Announcements", "Get notified about company announcements", "megaphone"],
  ["payroll", "Payroll", "Get notified about salary slips and payroll updates", "fileText"],
  ["attendance", "Attendance", "Get notified about attendance alerts and reminders", "calendar"],
  ["profile", "Profile & Account", "Get notified about profile and account changes", "user"],
  ["system", "System Notifications", "Receive important system messages and updates", "settings"],
];

const CATEGORY_ICON = {
  complaints: "chat",
  leave: "checkCircle",
  tasks: "clipboard",
  announcements: "megaphone",
  payroll: "fileText",
  attendance: "calendar",
  profile: "user",
  documents: "fileText",
  chat: "messageSquare",
  system: "infoCircle",
};

function buildSidebarNotificationCounts(payload = {}) {
  const counts = { ...(payload.counts || {}) };
  (payload.notifications || []).forEach((notification) => {
    if (!notification?.unread || !notification.category) return;
    const key = `${notification.category}Unread`;
    counts[key] = Math.max(Number(counts[key] || 0), 1);
  });
  return counts;
}

function timeAgo(value) {
  const diff = Date.now() - new Date(value).getTime();
  if (Number.isNaN(diff)) return "";
  const minutes = Math.max(1, Math.floor(diff / 60000));
  if (minutes < 60) return `${minutes} mins ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  return `${days} days ago`;
}

export default function EmployeeNotificationsPage({ token, mode = "list", onModeChange, onUnreadChange }) {
  const [notifications, setNotifications] = useState([]);
  const [counts, setCounts] = useState({});
  const [activeTab, setActiveTab] = useState("all");
  const [settings, setSettings] = useState(null);
  const [message, setMessage] = useState("");

  async function fetchNotifications() {
    if (!token) return;
    const payload = await getNotifications(token, {
      category: activeTab === "unread" ? "all" : activeTab,
      unread: activeTab === "unread",
    });
    setNotifications(payload.notifications || []);
    setCounts(payload.counts || {});
    onUnreadChange?.(buildSidebarNotificationCounts(payload));
  }

  async function fetchSettings() {
    if (!token) return;
    const payload = await getNotificationSettings(token);
    setSettings(payload.preferences);
  }

  useEffect(() => {
    const loadNotifications = async () => {
      if (mode === "list" && token) {
        await markNotificationRead(token, "all");
      }
      await fetchNotifications();
    };
    loadNotifications();
    const intervalId = window.setInterval(fetchNotifications, 3000);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeTab, mode]);

  useEffect(() => {
    if (mode === "settings") fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, token]);

  async function markAllRead() {
    await markNotificationRead(token, "all");
    await fetchNotifications();
  }

  async function saveSettings(nextSettings = settings) {
    const payload = await updateNotificationSettings(token, nextSettings);
    setSettings(payload.preferences);
    setMessage("Notification settings saved.");
  }

  const safeSettings = useMemo(() => settings || {
    channels: { inApp: true },
    preferences: {},
    quietHours: { enabled: true, startTime: "22:00", endTime: "07:00" },
  }, [settings]);

  if (mode === "settings") {
    return (
      <section className="employee-portal-page notification-page">
        <div className="notification-heading">
          <div>
            <h1>Notification Settings</h1>
            <div className="employee-breadcrumb"><span>Dashboard</span><span>{">"}</span><span>Notification Settings</span></div>
          </div>
          <button className="notification-outline-button" type="button" onClick={() => onModeChange?.("list")}>
            <AppIcon name="arrowLeft" /> Back to Notifications
          </button>
        </div>
        <div className="notification-warning"><AppIcon name="infoCircle" /> Manage how and when you want to receive notifications.</div>
        {message ? <div className="employee-inline-alert success">{message}</div> : null}
        <div className="notification-settings-grid">
          <div className="notification-settings-stack">
            <article className="notification-card">
              <div className="notification-card-head"><h2>Notification Channels</h2><p>Choose how you want to receive notifications</p></div>
              <SettingRow icon="bell" title="In-App Notifications" text="Receive notifications inside the application" checked disabled />
            </article>
            <article className="notification-card quiet-hours-card">
              <div className="notification-card-head">
                <div><h2>Quiet Hours</h2><p>Do not send non-urgent notifications during these hours</p></div>
                <Toggle checked={safeSettings.quietHours?.enabled} onChange={(checked) => setSettings((current) => ({ ...safeSettings, quietHours: { ...(current?.quietHours || safeSettings.quietHours), enabled: checked } }))} />
              </div>
              <div className="quiet-hours-grid">
                <label>Start Time<input type="time" value={safeSettings.quietHours?.startTime || "22:00"} onChange={(event) => setSettings((current) => ({ ...safeSettings, quietHours: { ...(current?.quietHours || safeSettings.quietHours), startTime: event.target.value } }))} /></label>
                <label>End Time<input type="time" value={safeSettings.quietHours?.endTime || "07:00"} onChange={(event) => setSettings((current) => ({ ...safeSettings, quietHours: { ...(current?.quietHours || safeSettings.quietHours), endTime: event.target.value } }))} /></label>
              </div>
              <p>You will still receive urgent notifications</p>
            </article>
          </div>
          <article className="notification-card">
            <div className="notification-card-head">
              <div><h2>Notification Preferences</h2><p>Choose what notifications you want to receive</p></div>
              <label className="notification-select-all">Select All <input type="checkbox" checked={PREFERENCES.every(([key]) => safeSettings.preferences?.[key] !== false)} onChange={(event) => {
                const next = { ...safeSettings, preferences: Object.fromEntries(PREFERENCES.map(([key]) => [key, event.target.checked])) };
                setSettings(next);
              }} /></label>
            </div>
            {PREFERENCES.map(([key, title, text, icon]) => (
              <SettingRow key={key} icon={icon} title={title} text={text} checked={safeSettings.preferences?.[key] !== false} onChange={(checked) => setSettings((current) => ({ ...safeSettings, preferences: { ...(current?.preferences || safeSettings.preferences), [key]: checked } }))} toggle />
            ))}
          </article>
        </div>
        <div className="notification-footer-actions">
          <button className="notification-outline-button" type="button" onClick={fetchSettings}>Reset to Default</button>
          <button className="notification-primary-button" type="button" onClick={() => saveSettings()}>Save Changes</button>
        </div>
      </section>
    );
  }

  return (
    <section className="employee-portal-page notification-page">
      <div className="notification-heading">
        <div>
          <h1>Notifications</h1>
          <div className="employee-breadcrumb"><span>Dashboard</span><span>{">"}</span><span>Notifications</span></div>
        </div>
        <div className="notification-heading-actions">
          <button className="notification-outline-button" type="button" onClick={markAllRead}>Mark all as read</button>
          <button className="notification-outline-button yellow" type="button" onClick={() => onModeChange?.("settings")}><AppIcon name="settings" /> Notification Settings</button>
        </div>
      </div>
      <div className="notification-tabs">
        {TABS.map(([key, label]) => (
          <button key={key} className={activeTab === key ? "active" : ""} onClick={() => setActiveTab(key)} type="button">
            {label} <span>{key === "unread" ? counts.unread || 0 : key === "all" ? counts.all || 0 : counts[key] || 0}</span>
          </button>
        ))}
      </div>
      <div className="notification-list">
        {notifications.map((item) => (
          <article key={item._id} className={`notification-row ${item.unread ? "unread" : ""}`} onClick={async () => { await markNotificationRead(token, item._id); await fetchNotifications(); }}>
            <span className="notification-unread-dot" />
            <div className={`notification-icon ${item.category}`}><AppIcon name={CATEGORY_ICON[item.category] || "infoCircle"} /></div>
            <div className="notification-copy"><h3>{item.title}</h3><p>{item.message}</p></div>
            <time>{timeAgo(item.createdAt)}</time>
            <AppIcon name="chevronRight" />
          </article>
        ))}
        {!notifications.length ? <div className="notification-empty">No notifications found.</div> : null}
      </div>
    </section>
  );
}

function SettingRow({ icon, title, text, checked, onChange, disabled = false, toggle = false }) {
  return (
    <div className="notification-setting-row">
      <div className={`notification-icon ${icon}`}><AppIcon name={icon} /></div>
      <div><h3>{title}</h3><p>{text}</p></div>
      {toggle ? <Toggle checked={checked} onChange={onChange} /> : <input type="checkbox" checked={checked} disabled={disabled} readOnly />}
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return <button type="button" className={`notification-toggle ${checked ? "active" : ""}`} onClick={() => onChange?.(!checked)}><span /></button>;
}
